import { supabase } from '../supabase';

// Verificación en dos pasos (TOTP con app autenticadora) de Supabase Auth.
// Cada usuario tiene a lo más un factor TOTP verificado.

export type TotpEnrollment = {
  factorId: string;
  qrCode: string; // data URL de un SVG
  secret: string; // para ingresarlo a mano en la app
};

/** Factor TOTP verificado del usuario, o null si no activó la verificación. */
export const getVerifiedTotpFactorId = async (): Promise<string | null> => {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw error;
  return data.totp[0]?.id ?? null;
};

export type MfaStatus = {
  /** 'required': tiene un factor y la sesión aún no validó el código. */
  status: 'required' | 'ok';
  hasFactor: boolean;
};

export const getMfaStatus = async (): Promise<MfaStatus> => {
  const { data, error } =
    await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error) throw error;
  if (data.currentLevel === 'aal2') return { status: 'ok', hasFactor: true };
  // nextLevel sale del usuario guardado en la sesión, que no ve un factor
  // activado después desde otro navegador; listFactors lo pide al servidor.
  const hasFactor = Boolean(await getVerifiedTotpFactorId());
  return { status: hasFactor ? 'required' : 'ok', hasFactor };
};

export const startTotpEnrollment = async (
  issuer: string
): Promise<TotpEnrollment> => {
  // Una inscripción que quedó a medias (QR generado, código nunca ingresado)
  // deja un factor sin verificar; se borra antes de generar otro.
  const { data: factors, error: listError } =
    await supabase.auth.mfa.listFactors();
  if (listError) throw listError;
  for (const factor of factors.all) {
    if (factor.factor_type === 'totp' && factor.status === 'unverified') {
      const { error: unenrollError } = await supabase.auth.mfa.unenroll({
        factorId: factor.id,
      });
      if (unenrollError) throw unenrollError;
    }
  }

  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    issuer,
    friendlyName: `${issuer} ${new Date().toISOString()}`,
  });
  if (error) throw error;
  return {
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
  };
};

/** Valida un código de 6 dígitos; sirve para inscribir y para iniciar sesión. */
export const verifyTotpCode = async (factorId: string, code: string) => {
  const { error } = await supabase.auth.mfa.challengeAndVerify({
    factorId,
    code,
  });
  if (error) throw error;
};
